const PROJECT_LABELS = {
  main: 'Tracking Cars',
  island: 'Island',
  qm: 'QR (QM)',
  trustseal: 'TrustSeal',
};

const EVENT_TYPE_LABELS = {
  ip_block: { title: 'Address blocked', tech: 'IP block' },
  ip_block_threshold: { title: 'Address hit the block limit', tech: 'IP block' },
  burst_action: { title: 'Same action repeated too fast', tech: 'burst' },
  vin_pull: { title: 'Pulled many vehicle IDs', tech: 'VIN' },
  user_monitored: { title: 'Moved to the watch list' },
  registry_vehicle_updates_threshold: { title: 'Too many vehicle updates today', tech: 'registry' },
  registry_update_burst: { title: 'Too many vehicle updates in a minute', tech: 'registry' },
  vin_harvest_hourly: { title: 'Pulled many distinct vehicle IDs in an hour', tech: 'VIN' },
  vin_harvest_daily: { title: 'Pulled many distinct vehicle IDs today', tech: 'VIN' },
  vin_404_hunt: { title: 'Guessing vehicle IDs that do not exist', tech: 'VIN' },
  inbound_new_ip: { title: 'Partner key used from a new address', tech: 'IP' },
  inbound_geo_blocked: { title: 'Partner request blocked by country', tech: 'geo' },
  external_bypass_main: { title: 'External key used outside Island', tech: 'bypass' },
  officer_delete_burst: { title: 'Too many officer-basket delete attempts', tech: 'delete' },
  delete_blocked: { title: 'Island delete blocked', tech: 'delete' },
  registry_identity_change: { title: 'Make, year, or owner was changed', tech: 'registry' },
  registry_identity_burst: { title: 'Identity fields changed on many cars', tech: 'registry' },
  form_entry_human: { title: 'Certificate form filled at a human pace', tech: 'form' },
  form_entry_watch: { title: 'Certificate form filled faster than usual', tech: 'form' },
  form_entry_automation: { title: 'Certificate submitted without human fill time', tech: 'form' },
  rate_limit: { title: 'Too many requests', tech: 'rate limit' },
};

const SOURCE_LABELS = {
  rate_limit: { title: 'Too many requests', tech: 'rate limit' },
  inbound_rate_limit: { title: 'Too many inbound requests', tech: 'rate limit' },
  ai_bot: { title: 'AI scraper blocked', tech: 'AI bot' },
};

const SEVERITY_LABELS = {
  critical: 'Urgent',
  warning: 'Warning',
  info: 'Info',
};

const STATUS_LABELS = {
  active: 'Active',
  reverted: 'Returned to normal',
  expired: 'Expired',
};

export const EVENT_GROUPS = [
  {
    id: 'rate_limit',
    title: 'Too many requests',
    tech: 'rate limit',
    accent: '#b45309',
    tint: '#fffbeb',
    border: '#fde68a',
  },
  {
    id: 'ip_block',
    title: 'Address blocked',
    tech: 'IP block',
    accent: '#b91c1c',
    tint: '#fef2f2',
    border: '#fecaca',
  },
  {
    id: 'ai',
    title: 'AI scraper blocked',
    tech: 'AI bot',
    accent: '#6d28d9',
    tint: '#f5f3ff',
    border: '#ddd6fe',
  },
  {
    id: 'unusual',
    title: 'Unusual activity',
    tech: 'burst / VIN',
    accent: '#0369a1',
    tint: '#f0f9ff',
    border: '#bae6fd',
  },
];

export function formatLabel(title, tech) {
  if (!title) return '—';
  return tech ? `${title} (${tech})` : title;
}

export function titleCaseKey(value) {
  if (value == null || value === '') return '—';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function projectLabel(source) {
  if (!source) return '—';
  return PROJECT_LABELS[source] || titleCaseKey(source);
}

export function projectOptions(sources = []) {
  const keys = sources.length
    ? sources.filter((item) => !item.inactive).map((item) => item.source || item).filter(Boolean)
    : Object.keys(PROJECT_LABELS).filter((key) => key !== 'trustseal');
  return keys.map((key) => ({ value: key, label: projectLabel(key) }));
}

export function eventTypeLabel(type) {
  const entry = EVENT_TYPE_LABELS[type];
  if (!entry) return titleCaseKey(type);
  return formatLabel(entry.title, entry.tech);
}

export function isRateLimitSource(source) {
  const value = String(source || '');
  return value === 'rate_limit' || value === 'inbound_rate_limit' || value.startsWith('rate_limit:');
}

export function sourceLabel(source) {
  if (isRateLimitSource(source)) {
    const entry = source === 'inbound_rate_limit' ? SOURCE_LABELS.inbound_rate_limit : SOURCE_LABELS.rate_limit;
    return formatLabel(entry.title, entry.tech);
  }
  const entry = SOURCE_LABELS[source];
  if (!entry) return titleCaseKey(source);
  return formatLabel(entry.title, entry.tech);
}

export function severityLabel(severity) {
  return SEVERITY_LABELS[severity] || titleCaseKey(severity);
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || titleCaseKey(status);
}

export function blockSourceOf(row) {
  if (!row) return null;
  return row.source || row.payload?.source || null;
}

export function eventGroup(row) {
  const source = blockSourceOf(row);
  if (source === 'ai_bot') return 'ai';
  if (row?.type === 'rate_limit' || isRateLimitSource(source)) return 'rate_limit';
  if (row?.type === 'ip_block' || row?.type === 'ip_block_threshold') return 'ip_block';
  if (
    row?.type === 'burst_action'
    || row?.type === 'vin_pull'
    || row?.type === 'registry_vehicle_updates_threshold'
    || row?.type === 'registry_update_burst'
    || row?.type === 'vin_harvest_hourly'
    || row?.type === 'vin_harvest_daily'
    || row?.type === 'vin_404_hunt'
    || row?.type === 'inbound_new_ip'
    || row?.type === 'inbound_geo_blocked'
    || row?.type === 'external_bypass_main'
    || row?.type === 'officer_delete_burst'
    || row?.type === 'delete_blocked'
    || row?.type === 'registry_identity_change'
    || row?.type === 'registry_identity_burst'
    || row?.type === 'form_entry_human'
    || row?.type === 'form_entry_watch'
    || row?.type === 'form_entry_automation'
  ) {
    return 'unusual';
  }
  if (row?.type === 'user_monitored') return 'watch';
  return 'other';
}

export function ipGroup(row) {
  const source = blockSourceOf(row);
  if (source === 'ai_bot') return 'ai';
  if (isRateLimitSource(source)) return 'rate_limit';
  return 'ip_block';
}

export function groupMeta(id) {
  return EVENT_GROUPS.find((group) => group.id === id) || null;
}

export function groupLabel(id) {
  const group = groupMeta(id);
  if (!group) return titleCaseKey(id);
  return formatLabel(group.title, group.tech);
}

export function filterByGroup(rows, groupId, kind = 'event') {
  if (!groupId) return rows;
  const classify = kind === 'ip' ? ipGroup : eventGroup;
  return rows.filter((row) => classify(row) === groupId);
}

export function filterByProject(rows, source) {
  if (!source) return rows;
  return rows.filter((row) => (row.backend || row.source_system) === source);
}

export function securityEventPath(row) {
  const id = String(row?.id || '');
  if (id.startsWith('hit-')) {
    if (!row?.ip || !row?.backend) return null;
    return `/cybersecurity/ip/${row.backend}/${encodeURIComponent(row.ip)}`;
  }
  if (!row?.backend || !row?.id) return null;
  return `/cybersecurity/event/${row.backend}/${row.id}`;
}

const RAW_KEYS = [
  'inbound_rate_limit',
  'registry_vehicle_updates_threshold',
  'registry_update_burst',
  'vin_harvest_hourly',
  'vin_harvest_daily',
  'vin_404_hunt',
  'inbound_new_ip',
  'inbound_geo_blocked',
  'external_bypass_main',
  'officer_delete_burst',
  'delete_blocked',
  'registry_identity_change',
  'registry_identity_burst',
  'form_entry_human',
  'form_entry_watch',
  'form_entry_automation',
  'ip_block_threshold',
  'user_monitored',
  'burst_action',
  'vin_pull',
  'rate_limit',
  'ai_bot',
  'ip_block',
];

export function humanizeText(value) {
  if (value == null || value === '') return value;
  let text = String(value);
  for (const raw of RAW_KEYS) {
    if (!text.includes(raw)) continue;
    const label = SOURCE_LABELS[raw]
      ? formatLabel(SOURCE_LABELS[raw].title, SOURCE_LABELS[raw].tech)
      : eventTypeLabel(raw);
    text = text.split(raw).join(label);
  }
  return text;
}

export function matchesSmartQuery(row, query, extra = []) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const payload = row.payload || {};
  const hay = [
    row.ip,
    row.actor_key,
    row.user_id,
    row.user_email,
    payload.user_email,
    payload.login_hint,
    row.action,
    row.reason,
    row.status,
    statusLabel(row.status),
    row.type,
    eventTypeLabel(row.type),
    blockSourceOf(row),
    sourceLabel(blockSourceOf(row)),
    groupLabel(eventGroup(row)),
    groupLabel(ipGroup(row)),
    projectLabel(row.backend || row.source_system),
    row.severity,
    severityLabel(row.severity),
    ...extra,
  ]
    .filter((value) => value != null && value !== '' && value !== '—')
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((token) => hay.includes(token));
}
